import { ApiClient, ApiClientOptions, type ApiKeyType } from "@quotientjs/core"
import {
  AsyncStorageProvider,
  StorageProvider,
} from "@quotientjs/browser-storage"
import { DeviceIdStore } from "./stores/device-id"
import { SessionIdStore } from "./stores/session-id"
import { PersonIdStore } from "./stores/person-id"
import { BrowserFingerprintStore } from "./stores/browser-fingerprint"

const PERSON_ID_HEADER = "X-Quotient-Person-Id"
const SESSION_ID_HEADER = "X-Quotient-Session-Id"
const DEVICE_ID_HEADER = "X-Quotient-Device-Id"
const BROWSER_FINGERPRINT_HEADER = "X-Quotient-Browser-Fingerprint"

/**
 * Browser-specific API client that extends the core ApiClient
 * with browser storage and device tracking
 *
 * NOTE (amiller68): we're doing all this switching to make the BrowserApiClient
 *  out of the box compatible with both sync and async storage providers so
 *  we can get the shopify web pixel integration to work without having to
 *  implement a custom client implementation.
 */
export class BrowserApiClient extends ApiClient {
  private provider?: AsyncStorageProvider | StorageProvider

  constructor(options: ApiClientOptions) {
    options.supportedKeyTypes = ["public" as ApiKeyType]
    super(options)
  }

  setStorageProvider(provider: AsyncStorageProvider | StorageProvider) {
    this.provider = provider
  }

  /**
   * Override headers to add browser-specific headers from storage
   */
  protected async getHeaders(): Promise<Record<string, string>> {
    const headers = await super.getHeaders()

    if (!this.provider) {
      return headers
    }

    // Get person ID
    let personId: string | null = null
    if (this.provider.type === "sync") {
      personId = PersonIdStore.get(this.provider as StorageProvider)
    } else {
      personId = await PersonIdStore.getAsync(
        this.provider as AsyncStorageProvider,
      )
    }
    if (personId) {
      headers[PERSON_ID_HEADER] = personId
    }

    // Get browser fingerprint
    let fingerprint: string | null = null
    if (this.provider.type === "sync") {
      fingerprint = BrowserFingerprintStore.get(
        this.provider as StorageProvider,
      )
    } else {
      fingerprint = await BrowserFingerprintStore.getAsync(
        this.provider as AsyncStorageProvider,
      )
    }
    if (fingerprint) {
      headers[BROWSER_FINGERPRINT_HEADER] = fingerprint
    }

    // Get device ID
    let deviceId: string | null = null
    if (this.provider.type === "sync") {
      deviceId = DeviceIdStore.get(this.provider as StorageProvider)
    } else {
      deviceId = await DeviceIdStore.getAsync(
        this.provider as AsyncStorageProvider,
      )
    }
    if (deviceId) {
      headers[DEVICE_ID_HEADER] = deviceId
    }

    // Get session ID
    let sessionId: string | null = null
    if (this.provider.type === "sync") {
      sessionId = SessionIdStore.get(this.provider as StorageProvider)
    } else {
      sessionId = await SessionIdStore.getAsync(
        this.provider as AsyncStorageProvider,
      )
    }
    if (sessionId) {
      headers[SESSION_ID_HEADER] = sessionId
    }

    return headers
  }
}
